var __assign = (this && this.__assign) || Object.assign || function(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
        s = arguments[i];
        for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
            t[p] = s[p];
    }
    return t;
};
import * as Long from "long";
import { selectBalancingBuff } from "../balancingSelectors/selectBalancingBuffs";
import { IdResource } from "../constants/IdResource";
import { EventType } from "../features/balancing/types/BalancingCore";
import { TypeNumberRounding } from "../features/generic/round-format/TypeNumberRounding";
import { BuffContextType, BuffHierarchyType, BuffTargetType, } from "../utils/network/generated/protoTypes";
import { shallowDiffers } from "../utils/shallowDiffers";
import { utilBuff } from "../utils/utilBuff";
import { utilNumber } from "../utils/utilNumber";
import { selectActiveDiscountEventForBuildingId } from "./selectDiscountEvent";
import { selectActiveDiscountEventInfoByEventType } from "./selectEventInfo";
export var selectBuffsInfo = function (state, buffProps) {
    var buffs = state.playerInfo.buff || [];
    var similarBuffs = buffs.filter(function (value) {
        // creating simple object from value with protos for comparing
        return !shallowDiffers(__assign({}, value), __assign({}, value, buffProps));
    });
    var buff = similarBuffs[0];
    if (similarBuffs.length > 1) {
        buff = similarBuffs.reduce(utilBuff.sumBuffs);
    }
    return buff;
};
export var selectContractXpBuffAmount = function (state, target, amount) {
    var buffParam = {
        context: BuffContextType.CONTRACT,
        hierarchy: BuffHierarchyType.PLAYER,
        target: target,
    };
    var buff = selectBuffsInfo(state, buffParam);
    return utilBuff.calculateBuffAmount(buff, amount);
};
export var selectContractRevenueBuffAmount = function (state, typeId, amount) {
    var buffParam = {
        context: BuffContextType.CONTRACT,
        hierarchy: BuffHierarchyType.PLAYER,
        target: BuffTargetType.REVENUE,
        targetId: typeId,
    };
    var buff = selectBuffsInfo(state, buffParam);
    return utilBuff.calculateBuffAmount(buff, amount, TypeNumberRounding.Ceil);
};
export var selectProductionResourceBuffAmount = function (state, productId, amount) {
    var buffParam = {
        context: BuffContextType.PRODUCTION,
        hierarchy: BuffHierarchyType.PLAYER,
        target: BuffTargetType.PRODUCT,
        targetId: productId,
    };
    var buff = selectBuffsInfo(state, buffParam);
    return utilBuff.calculateBuffAmount(buff, amount);
};
export var selectExperienceProductionBuffAmount = function (state, amount) {
    var buffParam = {
        context: BuffContextType.PRODUCTION,
        hierarchy: BuffHierarchyType.FARM,
        target: BuffTargetType.XP,
    };
    var buff = selectBuffsInfo(state, buffParam);
    return utilBuff.calculateBuffAmount(buff, amount);
};
export var selectProductionResourceTotalBuffAmount = function (state, productId, amount, multiplier) {
    var buffProductAmount = selectProductionResourceBuffAmount(state, productId, amount);
    var roundedBuffAmount = utilNumber.multiplyRounding(amount, multiplier);
    return roundedBuffAmount.add(buffProductAmount);
};
export var selectProductionCostBuffAmount = function (state, productId, amount) {
    var buffParam = {
        context: BuffContextType.PRODUCTION,
        hierarchy: BuffHierarchyType.FARM,
        hierarchyId: state.baseInfo.farmId,
        target: BuffTargetType.PRODUCT,
        targetId: productId,
    };
    var buff = selectBuffsInfo(state, buffParam);
    return utilBuff.calculateBuffAmount(buff, amount);
};
export var selectProductionXpBuffAmount = function (state, amount) {
    var buffParam = {
        context: BuffContextType.PRODUCTION,
        hierarchy: BuffHierarchyType.FARM,
        hierarchyId: state.baseInfo.farmId,
        target: BuffTargetType.XP,
    };
    var buff = selectBuffsInfo(state, buffParam);
    return utilBuff.calculateBuffAmount(buff, amount);
};
export var selectConstructionXpBuffAmount = function (state, amount) {
    var buffParam = {
        context: BuffContextType.CONSTRUCTION,
        hierarchy: BuffHierarchyType.BUILDING_GROUP,
        hierarchyId: state.baseInfo.farmId,
        target: BuffTargetType.XP,
    };
    var buff = selectBuffsInfo(state, buffParam);
    return utilBuff.calculateBuffAmount(buff, amount);
};
export var selectConstructionTimeBuffAmount = function (state, amount) {
    var buffParam = {
        context: BuffContextType.CONSTRUCTION,
        hierarchy: BuffHierarchyType.FARM,
        hierarchyId: state.baseInfo.farmId,
        target: BuffTargetType.TIME,
    };
    var buff = selectBuffsInfo(state, buffParam);
    return buff != undefined ? utilBuff.applyTo(amount, buff).toNumber() : 0;
};
export var selectContractCooldownBuffAmount = function (state, amount) {
    var buffParam = {
        context: BuffContextType.CONTRACT,
        hierarchy: BuffHierarchyType.PLAYER,
        target: BuffTargetType.TIME,
        targetId: 1,
    };
    var buff = selectBuffsInfo(state, buffParam);
    return buff != undefined
        ? utilBuff.applyTo(amount, buff).toNumber()
        : amount instanceof Long
            ? amount.toNumber()
            : amount;
};
export var selectOfferBuffAmount = function (state, targetId, amount) {
    var buffParam = {
        context: BuffContextType.OFFER,
        hierarchy: BuffHierarchyType.PLAYER,
        target: BuffTargetType.REVENUE,
        targetId: targetId,
    };
    var buff = selectBuffsInfo(state, buffParam);
    return utilBuff.calculateBuffAmount(buff, amount);
};
export var selectHappinessBuffAmount = function (state, amount) {
    var buffParam = {
        context: BuffContextType.PRODUCTION,
        hierarchy: BuffHierarchyType.FARM,
        hierarchyId: state.baseInfo.farmId,
        target: BuffTargetType.HAPPINESS,
    };
    var buff = selectBuffsInfo(state, buffParam);
    return utilBuff.calculateBuffAmount(buff, 0);
};
export var selectWaterTowerBuffedAmount = function (state, amount) {
    var buffParam = {
        context: BuffContextType.PRODUCTION,
        hierarchy: BuffHierarchyType.PLAYER,
        target: BuffTargetType.PRODUCT,
        targetId: IdResource.Water,
    };
    var buff = selectBuffsInfo(state, buffParam);
    if (buff === undefined) {
        return amount;
    }
    return utilBuff.applyTo(amount, buff);
};
export var selectEventBuffByTargetId = function (state, event, targetId) {
    var buffs = event.balancing.eventBuff.map(function (eventBuff) { return selectBalancingBuff(eventBuff.buffID); });
    var buffType = buffs.find(function (buff) { return buff.targetId === targetId; });
    if (!buffType) {
        return undefined;
    }
    return utilBuff.convertToIBuffParam(buffType);
};
export var selectDiscountBuffPercentageAmount = function (state, event, targetId) {
    var buff = selectEventBuffByTargetId(state, event, targetId);
    if (!buff) {
        return;
    }
    return Long.fromValue(100)
        .subtract(utilBuff.applyTo(101, buff))
        .toNumber();
};
export var selectPrimeTimeBuffPercentageAmount = function (state, event) {
    if (!event || (event && !event.balancing)) {
        return;
    }
    var buffAmount = event.balancing.effect;
    return (buffAmount - 1) * 100;
};
export var selectActiveExpansionCostBuff = function (state, targetId) {
    var event = selectActiveDiscountEventInfoByEventType(state, EventType.ExpansionDiscount);
    if (!event) {
        return undefined;
    }
    return selectEventBuffByTargetId(state, event, targetId);
};
export var selectActiveBuildingCostBuff = function (state, buildingId, targetId) {
    var event = selectActiveDiscountEventForBuildingId(state, buildingId);
    if (!event) {
        return undefined;
    }
    return selectEventBuffByTargetId(state, event, targetId);
};
export var selectAndApplyActiveBuildingBuffedCost = function (state, buildingId, targetId, amount) {
    var buff = selectActiveBuildingCostBuff(state, buildingId, targetId);
    if (buff === undefined) {
        return amount;
    }
    return utilBuff.applyTo(amount, buff, TypeNumberRounding.Ceil);
};
export var selectCoopProjectBuffedLoyalty = function (state, baseAmount) {
    var foundBuff = state.coopInfo.buffs.find(function (buff) {
        return buff.hierarchy === BuffHierarchyType.ALLIANCE &&
            buff.context === BuffContextType.PROJECT &&
            buff.target === BuffTargetType.PRODUCT;
    });
    if (foundBuff !== undefined) {
        return utilBuff.applyTo(baseAmount, foundBuff);
    }
    return Long.fromValue(baseAmount);
};



//////////////////
// WEBPACK FOOTER
// ./src/selectors/selectBuffsInfo.ts
// module id = 54
// module chunks = 3